import { useState } from 'react'
import { Copy } from 'lucide-react'
import { toast } from 'sonner'
import { AppShell } from '@/components/layout/app-shell'
import { APP } from '@/lib/constants'

type Lang = 'curl' | 'go' | 'node' | 'python' | 'php'

export function IntegrationGuidePage() {
  const [lang, setLang] = useState<Lang>('curl')
  const code = SAMPLES[lang]

  return (
    <AppShell>
      <div className="mx-auto max-w-4xl">
        <h1 className="text-3xl font-bold">Guía de integración</h1>
        <p className="mt-2 text-sm text-fg-secondary">
          Conecta tu ERP a Taxor. Headers HMAC + Idempotency-Key en cada POST mutativo.
        </p>

        <div className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
          <h2 className="text-lg font-semibold">1. Autenticación HMAC-SHA256</h2>
          <p className="mt-2 text-sm text-fg-secondary">
            Cada request debe traer 5 headers + el header de idempotency:
          </p>
          <pre className="mt-3 overflow-auto rounded-md bg-bg-elevated p-4 text-xs font-mono">{`X-Taxor-Client-Id: <UUID del cliente>
X-Taxor-Api-Key: tk_live_<hex32>
X-Taxor-Timestamp: <RFC3339 UTC, tolerancia ±5 min>
X-Taxor-Nonce: <UUID único, válido 15 min>
X-Taxor-Signature: <hex HMAC-SHA256>
Idempotency-Key: <UUID estable por request>`}</pre>
          <p className="mt-3 text-xs text-fg-muted">
            <strong>Signature:</strong> <code>HMAC-SHA256(hmac_secret, '&lt;timestamp&gt;.&lt;METHOD&gt;.&lt;path&gt;.&lt;sha256(body_bytes)&gt;')</code>
          </p>
        </div>

        <div className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">2. Ejemplo: emitir factura</h2>
            <div className="flex gap-1">
              {(['curl', 'go', 'node', 'python', 'php'] as const).map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  className={`rounded-md px-3 py-1 text-xs font-medium ${lang === l ? 'bg-brand-500 text-white' : 'border border-border text-fg-secondary hover:bg-bg-elevated'}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-3 relative">
            <pre className="overflow-auto rounded-md bg-bg-elevated p-4 text-xs font-mono">{code}</pre>
            <button
              onClick={() => { navigator.clipboard.writeText(code); toast.success('Copiado') }}
              className="absolute right-2 top-2 rounded-md border border-border bg-bg-surface p-1.5 hover:bg-bg-elevated"
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <div className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
          <h2 className="text-lg font-semibold">3. OpenAPI 3.0 spec</h2>
          <p className="mt-2 text-sm text-fg-secondary">Descarga el contrato completo:</p>
          <a href={`${APP.apiUrl}/openapi.yaml`} target="_blank" rel="noopener" className="mt-3 inline-block rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-bg-elevated">
            Descargar openapi.yaml →
          </a>
        </div>

        <div className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
          <h2 className="text-lg font-semibold">4. SLA + Changelog</h2>
          <ul className="mt-2 space-y-1 text-sm text-fg-secondary">
            <li><a href={`${APP.apiUrl}/api/v1/sla.json`} target="_blank" rel="noopener" className="text-brand-600 underline">/sla.json</a> — uptime, response time, incident response</li>
            <li><a href={`${APP.apiUrl}/api/v1/changelog`} target="_blank" rel="noopener" className="text-brand-600 underline">/changelog</a> — versiones + deprecation policy 12 meses</li>
          </ul>
        </div>
      </div>
    </AppShell>
  )
}

const SAMPLES: Record<Lang, string> = {
  curl: `# Calcular signature en bash:
TIMESTAMP=$(date -u +%Y-%m-%dT%H:%M:%SZ)
NONCE=$(uuidgen)
BODY='{"_meta":{"client_doc_ref":"ERP-001"},"Invoice":{"ID":"FE0012","IssueDate":"2026-05-10","IssueTime":"10:30:00-05:00","InvoiceTypeCode":"01","DocumentCurrencyCode":"COP","LineCountNumeric":1,"AccountingSupplierParty":{...},"AccountingCustomerParty":{...},"LegalMonetaryTotal":{...},"InvoiceLine":[...]}}'
BODY_HASH=$(echo -n "$BODY" | shasum -a 256 | cut -d' ' -f1)
PAYLOAD="$TIMESTAMP.POST./api/v1/documents.$BODY_HASH"
SIGNATURE=$(echo -n "$PAYLOAD" | openssl dgst -sha256 -hmac "$HMAC_SECRET" | cut -d' ' -f2)

curl -X POST https://api.taxor.co/api/v1/documents \\
  -H "X-Taxor-Client-Id: $CLIENT_ID" \\
  -H "X-Taxor-Api-Key: $API_KEY" \\
  -H "X-Taxor-Timestamp: $TIMESTAMP" \\
  -H "X-Taxor-Nonce: $NONCE" \\
  -H "X-Taxor-Signature: $SIGNATURE" \\
  -H "Idempotency-Key: $(uuidgen)" \\
  -H "Content-Type: application/json" \\
  -d "$BODY"`,

  go: `package main

import (
  "bytes"
  "crypto/hmac"
  "crypto/sha256"
  "encoding/hex"
  "fmt"
  "net/http"
  "time"
  "github.com/google/uuid"
)

func sendInvoice(body []byte) (*http.Response, error) {
  ts := time.Now().UTC().Format(time.RFC3339)
  nonce := uuid.NewString()
  bodyHash := fmt.Sprintf("%x", sha256.Sum256(body))
  payload := fmt.Sprintf("%s.POST./api/v1/documents.%s", ts, bodyHash)
  mac := hmac.New(sha256.New, []byte("HMAC_SECRET"))
  mac.Write([]byte(payload))
  sig := hex.EncodeToString(mac.Sum(nil))

  req, _ := http.NewRequest("POST", "https://api.taxor.co/api/v1/documents", bytes.NewReader(body))
  req.Header.Set("X-Taxor-Client-Id", "CLIENT_ID")
  req.Header.Set("X-Taxor-Api-Key", "API_KEY")
  req.Header.Set("X-Taxor-Timestamp", ts)
  req.Header.Set("X-Taxor-Nonce", nonce)
  req.Header.Set("X-Taxor-Signature", sig)
  req.Header.Set("Idempotency-Key", uuid.NewString())
  req.Header.Set("Content-Type", "application/json")
  return http.DefaultClient.Do(req)
}`,

  node: `import crypto from 'node:crypto'
import { randomUUID } from 'node:crypto'

async function sendInvoice(body) {
  const ts = new Date().toISOString()
  const bodyStr = JSON.stringify(body)
  const bodyHash = crypto.createHash('sha256').update(bodyStr).digest('hex')
  const payload = \`\${ts}.POST./api/v1/documents.\${bodyHash}\`
  const sig = crypto.createHmac('sha256', process.env.HMAC_SECRET).update(payload).digest('hex')

  return fetch('https://api.taxor.co/api/v1/documents', {
    method: 'POST',
    headers: {
      'X-Taxor-Client-Id': process.env.CLIENT_ID,
      'X-Taxor-Api-Key': process.env.API_KEY,
      'X-Taxor-Timestamp': ts,
      'X-Taxor-Nonce': randomUUID(),
      'X-Taxor-Signature': sig,
      'Idempotency-Key': randomUUID(),
      'Content-Type': 'application/json',
    },
    body: bodyStr,
  })
}`,

  python: `import hashlib, hmac, json, uuid
from datetime import datetime, timezone
import requests

def send_invoice(body: dict):
    ts = datetime.now(timezone.utc).strftime("%Y-%m-%dT%H:%M:%SZ")
    body_str = json.dumps(body)
    body_hash = hashlib.sha256(body_str.encode()).hexdigest()
    payload = f"{ts}.POST./api/v1/documents.{body_hash}"
    sig = hmac.new(HMAC_SECRET.encode(), payload.encode(), hashlib.sha256).hexdigest()
    return requests.post(
        "https://api.taxor.co/api/v1/documents",
        data=body_str,
        headers={
            "X-Taxor-Client-Id": CLIENT_ID,
            "X-Taxor-Api-Key": API_KEY,
            "X-Taxor-Timestamp": ts,
            "X-Taxor-Nonce": str(uuid.uuid4()),
            "X-Taxor-Signature": sig,
            "Idempotency-Key": str(uuid.uuid4()),
            "Content-Type": "application/json",
        },
    )`,

  php: `<?php
function sendInvoice(array $body): \\Psr\\Http\\Message\\ResponseInterface {
    $ts = gmdate('Y-m-d\\TH:i:s\\Z');
    $bodyStr = json_encode($body);
    $bodyHash = hash('sha256', $bodyStr);
    $payload = "$ts.POST./api/v1/documents.$bodyHash";
    $sig = hash_hmac('sha256', $payload, getenv('HMAC_SECRET'));

    $client = new \\GuzzleHttp\\Client();
    return $client->post('https://api.taxor.co/api/v1/documents', [
        'headers' => [
            'X-Taxor-Client-Id' => getenv('CLIENT_ID'),
            'X-Taxor-Api-Key' => getenv('API_KEY'),
            'X-Taxor-Timestamp' => $ts,
            'X-Taxor-Nonce' => Ramsey\\Uuid\\Uuid::uuid4()->toString(),
            'X-Taxor-Signature' => $sig,
            'Idempotency-Key' => Ramsey\\Uuid\\Uuid::uuid4()->toString(),
            'Content-Type' => 'application/json',
        ],
        'body' => $bodyStr,
    ]);
}`,
}
